import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import {
  undoAbandonRefMutationOptions,
  undoAbandonRefSchema,
} from "@/functions/refs/undo-abandon-ref";
import { useTRPC } from "@/integrations/trpc/react";
import { isDefined } from "@/utils/types";

export type UndoAbandonRefFormProps = {
  tournamentDivisionTeamId: number;
  refId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function UndoAbandonRefForm({
  tournamentDivisionTeamId,
  refId,
  ...props
}: UndoAbandonRefFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...undoAbandonRefMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      props.onOpenChange(false);
    },
  });

  const schema = undoAbandonRefSchema.omit({ id: true });

  const form = useAppForm({
    defaultValues: {},
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: () => {
      mutate({
        teamId: tournamentDivisionTeamId,
      });
    },
  });

  return (
    <Modal {...props}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Undo Abandon Ref?
        </Heading>

        <p className="mb-2 text-sm text-gray-700">
          Are you sure you want to undo marking this team as having abandoned
          ref duties?
        </p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to mark abandoned ref"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => props.onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton
                requireChange={false}
                isDisabled={!isDefined(refId)}
              >
                Confirm
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
